import {
  Button,
  Callout,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  useToast,
} from '@apovisio/ui';
import { invitationsCreateSchema } from '@apovisio/contracts';
import type { InvitationsCreated, MeetingDetail } from '@apovisio/contracts';
import { Copy, Link2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { SyntheticEvent } from 'react';
import { ApiClientError } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { apiFieldErrors, zodFieldErrors } from '../../lib/form-errors';
import type { FieldErrors } from '../../lib/form-errors';
import { useCreateInvitations, useRevokeInvitations } from './api';
import { InvitationStatusBadge } from './badges';

interface Row {
  key: number;
  label: string;
  email: string;
}

export function InvitationsCard({ meeting }: { meeting: MeetingDetail }) {
  const toast = useToast();
  const revoke = useRevokeInvitations(meeting.id);
  const [generating, setGenerating] = useState(false);
  const [created, setCreated] = useState<InvitationsCreated | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [pendingRevoke, setPendingRevoke] = useState<string[] | null>(null);
  const ended = meeting.status === 'ENDED';
  const active = meeting.invitations.filter((i) => i.status === 'ACTIVE');
  const labelOf = (id: string) => meeting.invitations.find((i) => i.id === id)?.label ?? '';

  function confirmRevoke() {
    if (!pendingRevoke) return;
    revoke.mutate(pendingRevoke, {
      onSuccess: ({ revoked }) => {
        toast.show({
          tone: 'success',
          title: revoked > 1 ? `${String(revoked)} invitations révoquées` : 'Invitation révoquée',
        });
        setSelected((current) => current.filter((id) => !pendingRevoke.includes(id)));
        setPendingRevoke(null);
      },
      onError: (error) => {
        toast.show({
          tone: 'danger',
          title: 'Révocation impossible',
          description: error instanceof ApiClientError ? error.message : undefined,
        });
      },
    });
  }

  return (
    <Card
      title="Invitations"
      description="Un lien privé par invité, révocable individuellement."
      actions={
        ended ? undefined : (
          <Button
            variant="secondary"
            icon={<Plus />}
            onClick={() => {
              setGenerating(true);
            }}
          >
            Générer
          </Button>
        )
      }
    >
      {meeting.invitations.length === 0 ? (
        <EmptyState
          icon={<Link2 />}
          headingLevel={3}
          title="Aucune invitation"
          description="Générez un lien privé par invité, puis transmettez-le vous-même."
        />
      ) : (
        <div className="app-table-wrap">
          <table className="app-table">
            <thead>
              <tr>
                <th scope="col">
                  <span className="av-visually-hidden">Sélection</span>
                </th>
                <th scope="col">Libellé</th>
                <th scope="col">E-mail</th>
                <th scope="col">Statut</th>
                <th scope="col">Expire le</th>
                <th scope="col">
                  <span className="av-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {meeting.invitations.map((invitation) => (
                <tr key={invitation.id}>
                  <td>
                    <input
                      type="checkbox"
                      className="app-table__check"
                      aria-label={`Sélectionner ${invitation.label}`}
                      disabled={invitation.status !== 'ACTIVE' || ended}
                      checked={selected.includes(invitation.id)}
                      onChange={(event) => {
                        setSelected((current) =>
                          event.target.checked
                            ? [...current, invitation.id]
                            : current.filter((id) => id !== invitation.id),
                        );
                      }}
                    />
                  </td>
                  <td>{invitation.label}</td>
                  <td>{invitation.email ?? '—'}</td>
                  <td>
                    <InvitationStatusBadge status={invitation.status} />
                  </td>
                  <td>{formatDateTime(invitation.expiresAt)}</td>
                  <td>
                    {invitation.status === 'ACTIVE' && !ended ? (
                      <Button
                        variant="ghost"
                        aria-label={`Révoquer ${invitation.label}`}
                        onClick={() => {
                          setPendingRevoke([invitation.id]);
                        }}
                      >
                        Révoquer
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {active.length > 0 && !ended ? (
        <div className="av-row">
          <Button
            variant="secondary"
            icon={<Trash2 />}
            disabled={selected.length === 0}
            onClick={() => {
              setPendingRevoke(selected);
            }}
          >
            Révoquer la sélection{selected.length > 0 ? ` (${String(selected.length)})` : ''}
          </Button>
        </div>
      ) : null}

      <GenerateInvitationsDialog
        open={generating}
        meeting={meeting}
        onClose={() => {
          setGenerating(false);
        }}
        onCreated={(result) => {
          setGenerating(false);
          setCreated(result);
        }}
      />
      <CreatedLinksDialog
        created={created}
        onClose={() => {
          setCreated(null);
        }}
      />
      <ConfirmDialog
        open={pendingRevoke !== null}
        title={
          pendingRevoke && pendingRevoke.length > 1
            ? `Révoquer ${String(pendingRevoke.length)} invitations ?`
            : `Révoquer l’invitation « ${labelOf(pendingRevoke?.[0] ?? '')} » ?`
        }
        description="Le lien cessera immédiatement de fonctionner. Une personne déjà dans la salle en sera retirée et ne pourra pas revenir."
        confirmLabel="Révoquer"
        busy={revoke.isPending}
        onConfirm={confirmRevoke}
        onCancel={() => {
          setPendingRevoke(null);
        }}
      />
    </Card>
  );
}

let rowKey = 0;
const newRow = (): Row => ({ key: rowKey++, label: '', email: '' });

function GenerateInvitationsDialog({
  open,
  meeting,
  onClose,
  onCreated,
}: {
  open: boolean;
  meeting: MeetingDetail;
  onClose: () => void;
  onCreated: (created: InvitationsCreated) => void;
}) {
  const create = useCreateInvitations(meeting.id);
  const [rows, setRows] = useState<Row[]>(() => [newRow()]);
  const [errors, setErrors] = useState<FieldErrors>({});

  const close = () => {
    setRows([newRow()]);
    setErrors({});
    onClose();
  };

  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = { invitations: rows.map(({ label, email }) => ({ label, email })) };
    const parsed = invitationsCreateSchema.safeParse(payload);
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      return;
    }
    create.mutate(payload, {
      onSuccess: (result) => {
        setRows([newRow()]);
        setErrors({});
        onCreated(result);
      },
      onError: (error) => {
        const fields = apiFieldErrors(error);
        setErrors(
          Object.keys(fields).length > 0
            ? fields
            : { _: error instanceof ApiClientError ? error.message : 'Génération impossible.' },
        );
      },
    });
  }

  const formId = `generate-${meeting.id}`;
  return (
    <Dialog
      open={open}
      onClose={close}
      size="md"
      title="Générer des invitations"
      description="Un lien privé par invité. L’e-mail est facultatif et n’est jamais utilisé pour un envoi automatique."
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            Annuler
          </Button>
          <Button type="submit" form={formId} loading={create.isPending}>
            Générer {rows.length > 1 ? `${String(rows.length)} liens` : 'le lien'}
          </Button>
        </>
      }
    >
      <form id={formId} className="av-stack" onSubmit={submit} noValidate>
        {errors._ ? <Callout tone="danger" title={errors._} live="alert" /> : null}
        {rows.map((row, index) => (
          <fieldset key={row.key} className="app-invite-row">
            <legend className="av-visually-hidden">Invité {index + 1}</legend>
            <Input
              label={`Libellé de l’invité ${String(index + 1)}`}
              placeholder="Cécile — ABG"
              maxLength={80}
              value={row.label}
              onChange={(event) => {
                setRows((current) =>
                  current.map((r) => (r.key === row.key ? { ...r, label: event.target.value } : r)),
                );
              }}
              error={errors[`invitations.${String(index)}.label`]}
            />
            <Input
              label={`E-mail (facultatif)`}
              type="email"
              autoComplete="off"
              value={row.email}
              onChange={(event) => {
                setRows((current) =>
                  current.map((r) => (r.key === row.key ? { ...r, email: event.target.value } : r)),
                );
              }}
              error={errors[`invitations.${String(index)}.email`]}
            />
            {rows.length > 1 ? (
              <Button
                variant="ghost"
                aria-label={`Retirer l’invité ${String(index + 1)}`}
                onClick={() => {
                  setRows((current) => current.filter((r) => r.key !== row.key));
                }}
              >
                Retirer
              </Button>
            ) : null}
          </fieldset>
        ))}
        {rows.length < 50 ? (
          <div>
            <Button
              variant="ghost"
              icon={<Plus />}
              onClick={() => {
                setRows((current) => [...current, newRow()]);
              }}
            >
              Ajouter un invité
            </Button>
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}

function CreatedLinksDialog({
  created,
  onClose,
}: {
  created: InvitationsCreated | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const [copied, setCopied] = useState<string[]>([]);
  const copy = (text: string, id: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied((current) => [...current, id]);
        toast.show({ tone: 'success', title: `Lien de ${label} copié` });
      },
      () => {
        toast.show({
          tone: 'danger',
          title: 'Copie impossible',
          description: 'Sélectionnez le lien puis copiez-le manuellement.',
        });
      },
    );
  };
  const count = created?.invitations.length ?? 0;
  return (
    <Dialog
      open={created !== null}
      onClose={onClose}
      size="md"
      title={count > 1 ? `${String(count)} liens générés` : 'Lien généré'}
      actions={
        <Button onClick={onClose} data-autofocus="">
          J’ai copié {count > 1 ? 'les liens' : 'le lien'}
        </Button>
      }
    >
      <div className="av-stack">
        <Callout tone="warning" title="Ces liens ne seront plus affichés.">
          Copiez-les maintenant et transmettez chacun à son seul destinataire.
        </Callout>
        <ul className="app-list">
          {created?.invitations.map((invitation) => (
            <li key={invitation.id}>
              <div className="av-stack av-stack--sm app-link-row">
                <strong>{invitation.label}</strong>
                <input
                  className="av-input app-link-row__url"
                  readOnly
                  value={invitation.url}
                  aria-label={`Lien de ${invitation.label}`}
                  onFocus={(event) => {
                    event.target.select();
                  }}
                />
              </div>
              <Button
                variant="secondary"
                icon={<Copy />}
                aria-label={`${copied.includes(invitation.id) ? 'Copié' : 'Copier'} le lien de ${invitation.label}`}
                onClick={() => {
                  copy(invitation.url, invitation.id, invitation.label);
                }}
              >
                {copied.includes(invitation.id) ? 'Copié' : 'Copier'}
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}
